import type { ParsedSlide, PptxFile, PptxFileUpdate } from '../types';
import API from './api';

// Get all PPTX files (user's files and public files)
export const getAllPptxFiles = async (tagIds?: string[]): Promise<PptxFile[]> => {
  const params: any = {};
  if (tagIds && tagIds.length > 0) {
    params.tagIds = tagIds.join(',');
  }
  const response = await API().get('/pptx-files', { params });
  return response.data;
};

// Get a specific PPTX file by ID
export const getPptxFile = async (id: string): Promise<PptxFile> => {
  const response = await API().get(`/pptx-files/${id}`);
  return response.data;
};

// Get the count of PPTX files
export const getPptxFileCount = async (): Promise<number> => {
  const response = await API().get('/pptx-files/count');
  return response.data.count;
};

// Upload a new PPTX file
export const uploadPptxFile = async (file: File): Promise<PptxFile> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await API().post('/pptx-files', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Update PPTX file metadata
export const updatePptxFile = async (id: string, updates: Partial<PptxFileUpdate>): Promise<PptxFile> => {
  const response = await API().put(`/pptx-files/${id}`, updates);
  return response.data;
};

// Delete a PPTX file
export const deletePptxFile = async (id: string): Promise<void> => {
  await API().delete(`/pptx-files/${id}`);
};

// Publish a PPTX file (make it public)
export const publishPptxFile = async (id: string): Promise<PptxFile> => {
  const response = await API().post(`/pptx-files/${id}/publish`);
  return response.data;
};

// Update a slide's description
export const updateSlideDescription = async (
  fileId: string,
  slideNumber: number,
  description: string
): Promise<ParsedSlide> => {
  const response = await API().put(
    `/pptx-files/${fileId}/slides/${slideNumber}/description`,
    { description }
  );
  return response.data;
};

// Generate description for a slide using AI
export const generateDescriptionFromSlide = async (
  fileId: string,
  slideNumber: number,
  { lang }: { lang: string }
): Promise<{ description: string }> => {
  const response = await API().get(
    `/pptx-files/${fileId}/slides/${slideNumber}/generate-description`,
    { params: { lang } }
  );
  return response.data;
};

// Upload a thumbnail for a slide
export const uploadThumbnail = async (fileId: string, slideNumber: number, file: File): Promise<void> => {
  console.log(`Uploading thumbnail for slide ${slideNumber}:`, {
    fileId,
    fileName: file.name,
    fileSize: file.size,
  });

  const formData = new FormData();
  formData.append('thumbnail', file);

  try {
    await API({feedbackMode: 'off'}).post(`/pptx-files/${fileId}/thumbnails/${slideNumber}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    console.log(`Successfully uploaded thumbnail for slide ${slideNumber}`);
  } catch (error) {
    console.error(`Failed to upload thumbnail for slide ${slideNumber}:`, error);
    throw error;
  }
};

// Delete a slide's thumbnail
export const deleteThumbnail = async (fileId: string, slideNumber: number): Promise<void> => {
  await API().delete(`/pptx-files/${fileId}/thumbnails/${slideNumber}`);
};

// Generate description for the entire PPTX file using AI
export const generatePptxDescription = async (fileId: string, { lang }: { lang: string }): Promise<{ description: string }> => {
  const response = await API().get(`/pptx-files/${fileId}/generate-description`, { params: { lang } });
  return response.data;
};

// Update a slide element's locked status
export const updateSlideElementLocked = async (
  fileId: string, 
  slideNumber: number, 
  elementIndex: number, 
  isLocked: boolean
): Promise<ParsedSlide> => {
  const response = await API().put(
    `/pptx-files/${fileId}/slides/${slideNumber}/element-locked`,
    { elementIndex, isLocked }
  );
  return response.data;
};
